import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import {
  Add,
  ArrowForward,
  AssignmentOutlined,
  CheckCircleOutline,
  Close,
  CloudUploadOutlined,
  Delete,
  EditOutlined,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatUnderlined,
  History,
  ImageOutlined,
  InsertLink,
  LayersOutlined,
  LockOutlined,
  PauseCircleOutline,
  PendingOutlined,
  PersonOutline,
  Refresh,
  ReplayOutlined,
  Search,
  VerifiedUserOutlined,
  Visibility,
} from "@mui/icons-material";
import AppPagination from "components/AppPagination";
import ConfirmationDialog from "components/ConfirmationDialog";
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { isSupportButtonAllowed } from "config/ConfigAccessButton";
import { usePageTitle } from "contexts/PageTitleContext";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { getNavigations } from "layouts/layout-parts/navigation";
import {
  notifySupportRequestCreated,
} from "utils/supportNotifications";

const MAX_PHOTOS = 3;
const MAX_ATTACHMENT_BYTES = 150 * 1024 * 1024; // 150MB

const isMovLikeFile = (file) => {
  if (!file) return false;
  const name = String(file.name || "")
    .normalize("NFKC")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim()
    .toLowerCase();
  const type = String(file.type || "").toLowerCase();
  return (
    name.endsWith(".mov") ||
    name.includes(".mov.") ||
    type.includes("quicktime") ||
    type === "video/mov"
  );
};

/** Backend rejects .mov — always upload Mac recordings with an .mp4 filename. */
const preparePhotoUploadPart = async (file) => {
  const buffer = await file.arrayBuffer();
  let filename = String(file.name || "attachment")
    .normalize("NFKC")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim();
  let mime = file.type || "application/octet-stream";

  if (isMovLikeFile(file) || /\.mov$/i.test(filename)) {
    const base =
      filename.replace(/\.mov$/i, "").replace(/\.[^./\\]+$/i, "").trim() ||
      "recording";
    filename = `${base}.mp4`;
    mime = "video/mp4";
  }

  // Explicit Blob + filename so multipart Content-Disposition cannot keep .mov
  const blob = new Blob([buffer], { type: mime });
  return { blob, filename };
};

const DEFAULT_STATUSES = [
  "New",
  "Assigned",
  "In Progress",
  "On Hold",
  "Resolved",
  "Verification",
  "Closed",
  "Reopened",
];
// Backend allows only one-step transitions (e.g. New → Assigned only)
const STATUS_FLOW = {
  New: ["Assigned"],
  Assigned: ["In Progress"],
  "In Progress": ["On Hold", "Resolved"],
  "On Hold": ["In Progress", "Resolved"],
  Resolved: ["Verification"],
  Verification: ["Closed"],
  Closed: [],
  Reopened: ["Assigned"],
  // legacy statuses (older tickets)
  Pending: ["Assigned", "In Progress"],
  Complete: ["Done", "Closed"],
  Done: [],
};
const pageSizeOptions = [10, 25, 50, 100];

const ISSUE_TYPE_OPTIONS = [
  "Bug / Issue",
  "Feature Request",
  "Change Request",
  "Support / Query",
];

const ENVIRONMENT_OPTIONS = [
  "Production (IshVed Team)",
  "UAT (Ai Project Manager Only)",
];

const PRIORITY_OPTIONS = [
  { value: "Critical", helper: "System/business is blocked" },
  { value: "High", helper: "Major functionality is affected" },
  { value: "Medium", helper: "Issue exists but workaround is available" },
  { value: "Low", helper: "Minor issue/UI improvement" },
];

const WORKFLOW_STEPS = [
  { title: "New", desc: "Ticket is submitted", icon: AssignmentOutlined },
  { title: "Assigned", desc: "Developer is assigned", icon: PersonOutline },
  { title: "In Progress", desc: "Developer starts working", icon: PendingOutlined },
  { title: "On Hold", desc: "Waiting for info / approval", icon: PauseCircleOutline },
  { title: "Resolved", desc: "Developer fixes the issue", icon: CheckCircleOutline },
  { title: "Verification", desc: "Requester/UAT verifies", icon: VerifiedUserOutlined },
  { title: "Closed", desc: "Ticket is closed", icon: LockOutlined },
  { title: "Reopened", desc: "Issue not fixed, reopened", icon: ReplayOutlined },
];

const FilterCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 14,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
  position: "sticky",
  top: 80,
  zIndex: 10,
}));

const FilterHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: alpha(theme.palette.primary.main, 0.02),
  borderRadius: "14px 14px 0 0",
}));

const FormCard = styled(Card)(({ theme }) => ({
  borderRadius: 14,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
  backgroundImage: "none",
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(2.5),
}));

const WorkflowCard = styled(Card)(({ theme }) => ({
  borderRadius: 14,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
  backgroundImage: "none",
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(2.5),
}));

const StyledFormField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 10,
    backgroundColor: theme.palette.background.paper,
  },
}));

const StyledSelect = styled(FormControl)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 10,
    backgroundColor: theme.palette.background.paper,
  },
}));

const StatusTabs = styled(Box)(() => ({
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  alignItems: "flex-end",
}));

const StatusTab = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  minHeight: 36,
  borderRadius: "8px 8px 0 0",
  textTransform: "none",
  fontWeight: 700,
  padding: "8px 14px",
  fontSize: "0.875rem",
  ...(active
    ? {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        color: theme.palette.primary.main,
        borderBottom: `2px solid ${theme.palette.primary.main}`,
      }
    : {
        color: theme.palette.text.secondary,
        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          color: theme.palette.primary.main,
        },
      }),
}));

const FieldLabel = ({ step, label, required = false }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        bgcolor: "primary.main",
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {step}
    </Box>
    <Typography variant="body2" fontWeight={700} color="text.primary">
      {label}
      {required ? (
        <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>
          *
        </Box>
      ) : null}
    </Typography>
  </Stack>
);

const getApiErrorMessage = (error, fallback = "Something went wrong") => {
  if (!error) return fallback;
  if (typeof error === "string") return error;

  if (error.errors && typeof error.errors === "object") {
    const parts = Object.entries(error.errors).flatMap(([key, value]) => {
      if (Array.isArray(value)) return value.map((item) => `${key}: ${item}`);
      if (value && typeof value === "object") return [`${key}: ${JSON.stringify(value)}`];
      return [`${key}: ${value}`];
    });
    if (parts.length) return parts.join(" | ");
  }
  if (error.data?.errors && typeof error.data.errors === "object") {
    const parts = Object.entries(error.data.errors).flatMap(([key, value]) => {
      if (Array.isArray(value)) return value.map((item) => `${key}: ${item}`);
      if (value && typeof value === "object") return [`${key}: ${JSON.stringify(value)}`];
      return [`${key}: ${value}`];
    });
    if (parts.length) return parts.join(" | ");
  }

  if (error.message && typeof error.message === "string" && error.message !== "Validation failed") {
    return error.message;
  }
  if (error.detail) return String(error.detail);
  if (error.error) return String(error.error);
  if (error.message) return String(error.message);
  if (typeof error === "object") {
    const first = Object.values(error).flat?.()?.[0] || Object.values(error)[0];
    if (typeof first === "string") return first;
    if (Array.isArray(first) && first[0]) return String(first[0]);
  }
  return fallback;
};

const extractRows = (response) => {
  if (!response || typeof response !== "object") return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.results)) return response.results;
  if (Array.isArray(response.data)) return response.data;
  const data = response.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    if (Array.isArray(data.results)) return data.results;
    if (Array.isArray(data.data)) return data.data;
    for (const key of ["support_requests", "tickets", "requests", "SupportRequest"]) {
      if (Array.isArray(data[key])) return data[key];
    }
    const firstArray = Object.values(data).find((v) => Array.isArray(v));
    if (firstArray) return firstArray;
  }
  return [];
};

const extractPagination = (response) => {
  if (!response || typeof response !== "object") return null;
  return (
    response.pagination ||
    response.meta ||
    (response.data &&
      typeof response.data === "object" &&
      !Array.isArray(response.data) &&
      response.data.pagination) ||
    null
  );
};

const extractDetail = (response) => {
  if (!response || typeof response !== "object") return null;
  if (response.data && typeof response.data === "object" && !Array.isArray(response.data)) {
    return response.data;
  }
  return response;
};

const normalizeChoiceList = (raw) => {
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === "string") return item;
        return item?.name || item?.label || item?.value || item?.status || "";
      })
      .filter(Boolean);
  }
  if (raw && typeof raw === "object") {
    return Object.values(raw)
      .map((item) => (typeof item === "string" ? item : item?.name || item?.label || item?.value || ""))
      .filter(Boolean);
  }
  return [];
};

const normalizeStatusChoices = (response) => {
  const raw =
    response?.data?.statuses ||
    response?.data?.status_choices ||
    response?.data?.choices ||
    response?.statuses ||
    response?.status_choices ||
    response?.choices ||
    response?.data ||
    [];
  return normalizeChoiceList(raw);
};

const normalizeModuleFeatureChoices = (response) => {
  const raw =
    response?.data?.module_features ||
    response?.data?.module_feature_choices ||
    response?.data?.module_feature ||
    response?.module_features ||
    response?.module_feature_choices ||
    response?.module_feature ||
    [];
  return normalizeChoiceList(raw);
};

const normalizeModuleLabel = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Dashboard / CMD Dashboard by role; other modules follow sidebar access. */
const filterModuleFeatureOptionsForUser = (options) => {
  let userRole = "";
  let navs = [];
  try {
    const loggedData = JSON.parse(localStorage.getItem("logged_data")) || {};
    userRole = loggedData?.data?.user_type || "";
    navs = getNavigations() || [];
  } catch {
    navs = [];
  }

  const allowedNormNames = new Set(
    (navs || []).map((nav) => normalizeModuleLabel(nav?.name))
  );
  const hasDashboardModule = (navs || []).some((nav) => {
    const name = normalizeModuleLabel(nav?.name);
    return nav?.permission === "dashboard" || name === "dashboard" || name === "cmd dashboard";
  });

  const matchesAllowedNav = (optionKey) => {
    if (allowedNormNames.has(optionKey)) return true;
    const optionRoot = optionKey.replace(/s$/, "");
    for (const allowed of allowedNormNames) {
      const allowedRoot = allowed.replace(/s$/, "");
      if (allowedRoot === optionRoot) return true;
      if (allowed.includes(optionKey) || optionKey.includes(allowed)) return true;
    }
    return false;
  };

  return (options || []).filter((option) => {
    const key = normalizeModuleLabel(option);
    if (key === "dashboard" || key === "cmd dashboard") {
      if (!hasDashboardModule) return false;
      if (userRole === "Admin") return true;
      if (userRole === "co_admin") return key === "cmd dashboard";
      return key === "dashboard";
    }
    return matchesAllowedNav(key);
  });
};

const getStatusColor = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "pending" || key === "new") return "info";
  if (key === "in progress" || key === "assigned") return "primary";
  if (key === "on hold") return "warning";
  if (key === "complete" || key === "resolved" || key === "verification") return "success";
  if (key === "done" || key === "closed") return "default";
  if (key === "reopened") return "error";
  return "default";
};

const getPriorityChipColor = (priority) => {
  const key = String(priority || "").toLowerCase();
  if (key === "critical" || key === "high") return "error";
  if (key === "medium") return "warning";
  if (key === "low") return "success";
  return "default";
};

const getTicketId = (ticket) =>
  ticket?.id ?? ticket?.support_id ?? ticket?.pk ?? ticket?.support_request_id;

const getTicketRaiserId = (ticket) => {
  if (!ticket || typeof ticket !== "object") return null;
  const nested =
    ticket.user ||
    ticket.created_by_user ||
    ticket.raised_by_user ||
    ticket.requester ||
    null;
  const candidates = [
    ticket.user_id,
    ticket.created_by_id,
    ticket.raised_by_id,
    ticket.raised_by,
    ticket.requester_id,
    ticket.created_user_id,
    ticket.owner_id,
    ticket.created_by,
    nested && typeof nested === "object" ? nested.id ?? nested.user_id : nested,
  ];
  for (const value of candidates) {
    if (value == null || typeof value === "object") continue;
    const id = String(value).trim();
    if (id) return id;
  }
  return null;
};

const isTicketRaisedByCurrentUser = (ticket) => {
  const currentUserId = String(localStorage.getItem("user_id") || "").trim();
  const raiserId = getTicketRaiserId(ticket);
  if (!currentUserId || !raiserId) return false;
  return currentUserId === raiserId;
};

const isClosedTicket = (ticket) =>
  String(ticket?.status || "").trim().toLowerCase() === "closed";

const getTicketCode = (ticket) => {
  const raw =
    ticket?.ticket_id ||
    ticket?.ticket_code ||
    ticket?.support_ticket_id ||
    null;
  if (raw != null && String(raw).trim()) return String(raw).trim();
  const numericId = getTicketId(ticket);
  if (numericId == null || numericId === "") return null;
  const asString = String(numericId);
  if (asString.toUpperCase().startsWith("NXG-TKT")) return asString;
  if (/^\d+$/.test(asString)) return `NXG-TKT-${asString.padStart(6, "0")}`;
  return asString;
};

const formatDisplayTicketId = (ticket) => getTicketCode(ticket) || "—";

const getTicketSubject = (ticket) =>
  ticket?.subject || ticket?.title || ticket?.ticket_subject || "—";

const formatDateTime = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const resolvePhotoUrls = (data) => {
  const photos = data?.photos || data?.images || [];
  if (!Array.isArray(photos)) return [];
  return photos
    .map((photo) => {
      if (typeof photo === "string") return apiService.resolveAuthenticatedFileUrl(photo);
      const url = photo?.url || photo?.photo || photo?.image || photo?.path || photo?.file;
      return url ? apiService.resolveAuthenticatedFileUrl(url) : null;
    })
    .filter(Boolean);
};

const isVideoAttachmentUrl = (url) =>
  /\.(mp4|mov|webm|m4v|ogg)(\?|#|$)/i.test(String(url || ""));

const AttachmentMedia = ({ url, index, sx = {} }) =>
  isVideoAttachmentUrl(url) ? (
    <Box
      component="video"
      src={url}
      controls
      preload="metadata"
      sx={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        bgcolor: "common.black",
        ...sx,
      }}
    />
  ) : (
    <Box
      component="img"
      src={url}
      alt={`Support attachment ${index + 1}`}
      sx={{ width: "100%", height: "100%", objectFit: "cover", ...sx }}
    />
  );

const emptyForm = () => ({
  title: "",
  description: "",
  photos: [],
  sources: [],
  issueType: "",
  moduleFeature: "",
  environment: "",
  priority: "",
});

const Support = () => {
  const { setPageTitle } = usePageTitle();
  const toast = useToast();
  const [permissionTick, setPermissionTick] = useState(0);

  useEffect(() => {
    const handlePermissionsUpdated = () => setPermissionTick((value) => value + 1);
    window.addEventListener("permissions-updated", handlePermissionsUpdated);
    return () => window.removeEventListener("permissions-updated", handlePermissionsUpdated);
  }, []);

  // permissionTick forces a re-read of localStorage after permissions are saved
  const can = (action) => {
    void permissionTick;
    return isSupportButtonAllowed(action);
  };
  const canCreate = can("create");
  const canEditRequest = can("edit_request");
  const canView = can("view");
  const canDelete = can("delete");
  const canUpdateStatus = can("update_status");
  const canViewAll = can("view_all");

  const [statusFilter, setStatusFilter] = useState("all");
  const [statusChoices, setStatusChoices] = useState(DEFAULT_STATUSES);
  const [moduleFeatureOptions, setModuleFeatureOptions] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const countInFlightRef = useRef(new Set());

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm());
  const [creating, setCreating] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [statusLogs, setStatusLogs] = useState([]);
  const [statusForm, setStatusForm] = useState({
    status: "",
    remark: "",
    assigned_to: "",
    resolution: "",
  });
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyForm());
  const [editing, setEditing] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState({ open: false, ticket: null });
  const [deleting, setDeleting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState({ open: false, urls: [], loading: false });

  useEffect(() => {
    setPageTitle("Raise Support Ticket");
  }, [setPageTitle]);

  const fetchStatusChoices = useCallback(async () => {
    try {
      const response = await apiService.get(API_ENDPOINTS.GET_support_status_choices);
      const choices = normalizeStatusChoices(response);
      if (choices.length) setStatusChoices(choices);
      const moduleFeatures = normalizeModuleFeatureChoices(response);
      if (moduleFeatures.length) setModuleFeatureOptions(moduleFeatures);
    } catch (error) {
      console.error("Failed to load support status choices:", error);
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", String(currentPage));
      params.append("page_size", String(pageSize));
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (searchTerm.trim()) params.append("search", searchTerm.trim());
      if (canViewAll) params.append("all", "true");

      const response = await apiService.get(
        `${API_ENDPOINTS.GET_support_requests}?${params.toString()}`
      );
      const rows = extractRows(response);
      const pagination = extractPagination(response);

      setTickets(rows);
      if (pagination) {
        const total =
          Number(
            pagination.total_items ||
              pagination.total_count ||
              pagination.count ||
              pagination.total ||
              rows.length
          ) || rows.length;
        setTotalPages(
          Number(pagination.total_pages || pagination.num_pages || pagination.pages || 1) || 1
        );
        setTotalItems(total);
        setStatusCounts((prev) => ({
          ...prev,
          [statusFilter || "all"]: total,
        }));
      } else {
        const count = Number(response?.count || response?.data?.count || rows.length) || rows.length;
        setTotalItems(count);
        setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
        setStatusCounts((prev) => ({
          ...prev,
          [statusFilter || "all"]: count,
        }));
      }
    } catch (error) {
      console.error("Failed to load support requests:", error);
      toast.showError(getApiErrorMessage(error, "Failed to load support requests"));
      setTickets([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [canViewAll, currentPage, pageSize, searchTerm, statusFilter, toast]);

  const fetchStatusCount = useCallback(async (status = statusFilter) => {
    const statusKey = status || "all";
    const requestKey = `${statusKey}|${searchTerm.trim()}|${canViewAll ? "all" : "own"}`;
    if (countInFlightRef.current.has(requestKey)) return;
    countInFlightRef.current.add(requestKey);

    try {
      const params = new URLSearchParams();
      if (statusKey !== "all") params.append("status", statusKey);
      if (searchTerm.trim()) params.append("search", searchTerm.trim());
      if (canViewAll) params.append("all", "true");
      const qs = params.toString();
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_support_status_count}${qs ? `?${qs}` : ""}`
      );

      const payload = response?.data?.data ?? response?.data ?? response;
      const nextCounts = {};

      const statusesList = Array.isArray(payload?.statuses)
        ? payload.statuses
        : Array.isArray(payload)
          ? payload
          : [];
      if (statusesList.length) {
        statusesList.forEach((row) => {
          const name = row?.status || row?.name || row?.value || row?.label;
          if (!name) return;
          nextCounts[name] = Number(row?.count ?? row?.total ?? row?.total_count ?? 0) || 0;
        });
      } else if (payload && typeof payload === "object" && !Array.isArray(payload)) {
        const nested = payload.counts && typeof payload.counts === "object" ? payload.counts : payload;
        const knownKeys = new Set(["count", "total", "total_count", "success", "message", "data"]);
        Object.entries(nested).forEach(([key, value]) => {
          if (knownKeys.has(key)) return;
          if (typeof value === "number" || (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value)))) {
            nextCounts[key] = Number(value) || 0;
          }
        });
      }

      const hasSingleCount =
        payload?.count != null ||
        payload?.total != null ||
        payload?.total_count != null ||
        response?.count != null;
      if (hasSingleCount) {
        nextCounts[statusKey] = Number(
          payload?.count ?? payload?.total ?? payload?.total_count ?? response?.count ?? 0
        ) || 0;
      }

      setStatusCounts((prev) => ({ ...prev, ...nextCounts }));
    } catch (error) {
      console.error(`Failed to load support count for ${statusKey}:`, error);
    } finally {
      countInFlightRef.current.delete(requestKey);
    }
  }, [canViewAll, searchTerm, statusFilter]);

  useEffect(() => {
    fetchStatusChoices();
  }, [fetchStatusChoices]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    fetchStatusCount(statusFilter);
  }, [fetchStatusCount, statusFilter]);

  const statusTabs = useMemo(
    () => [{ value: "all", label: "All" }, ...statusChoices.map((s) => ({ value: s, label: s }))],
    [statusChoices]
  );

  const visibleModuleFeatureOptions = useMemo(
    () => filterModuleFeatureOptionsForUser(moduleFeatureOptions),
    [moduleFeatureOptions, permissionTick]
  );

  const getNextStatusesForTicket = (ticket) => {
    const current = String(ticket?.status || "").trim();
    if (!current) return [];
    // Original raiser can reopen Closed tickets via Update Status
    if (isClosedTicket(ticket) && isTicketRaisedByCurrentUser(ticket)) {
      return ["Reopened"];
    }
    if (Object.prototype.hasOwnProperty.call(STATUS_FLOW, current)) {
      return STATUS_FLOW[current];
    }
    const matchedKey = Object.keys(STATUS_FLOW).find(
      (key) => key.toLowerCase() === current.toLowerCase()
    );
    return matchedKey ? STATUS_FLOW[matchedKey] : [];
  };

  const resolveStatusCountKey = (status) => {
    const raw = String(status || "").trim();
    if (!raw) return null;
    if (Object.prototype.hasOwnProperty.call(statusCounts, raw)) return raw;
    const fromChoices = statusChoices.find(
      (choice) => String(choice).toLowerCase() === raw.toLowerCase()
    );
    if (fromChoices) return fromChoices;
    const fromCounts = Object.keys(statusCounts).find(
      (key) => key.toLowerCase() === raw.toLowerCase()
    );
    return fromCounts || raw;
  };

  const bumpStatusCountsOnMove = (fromStatus, toStatus) => {
    const fromKey = resolveStatusCountKey(fromStatus);
    const toKey = resolveStatusCountKey(toStatus);
    if (!fromKey && !toKey) return;
    if (fromKey && toKey && fromKey.toLowerCase() === toKey.toLowerCase()) return;

    setStatusCounts((prev) => {
      const next = { ...prev };
      if (fromKey) {
        next[fromKey] = Math.max(0, Number(next[fromKey] || 0) - 1);
      }
      if (toKey) {
        next[toKey] = Number(next[toKey] || 0) + 1;
      }
      return next;
    });
  };

  const refreshStatusCountsAfterMove = (fromStatus, toStatus) => {
    bumpStatusCountsOnMove(fromStatus, toStatus);
    fetchStatusCount("all");
  };

  const nextStatuses = useMemo(
    () => getNextStatusesForTicket(detail),
    [detail]
  );

  const openStatusUpdate = (ticket) => {
    if (!canUpdateStatus) return;
    const flow = getNextStatusesForTicket(ticket);
    if (!flow.length) {
      toast.showError("No status update available for this ticket");
      return;
    }
    setDetail(ticket);
    setStatusForm({
      status: flow[0] || "",
      remark: "",
      resolution: "",
      assigned_to:
        ticket?.assigned_to != null
          ? String(ticket.assigned_to)
          : localStorage.getItem("user_id") || "",
    });
    setStatusUpdateOpen(true);
  };

  const openCreate = () => {
    setCreateForm(emptyForm());
    setCreateOpen(true);
  };

  const handleCancelCreate = () => {
    if (creating) return;
    setCreateForm(emptyForm());
    setCreateOpen(false);
  };

  const handlePhotoSelect = (files, target = "create") => {
    const selected = Array.from(files || [])
      .filter((file) => {
        if (file.size > MAX_ATTACHMENT_BYTES) {
          toast.showError(`"${file.name}" exceeds the 150MB limit`);
          return false;
        }
        return true;
      })
      .map((file) => {
        if (!isMovLikeFile(file)) return file;
        const base =
          String(file.name || "recording")
            .replace(/\.mov$/i, "")
            .replace(/\.[^./\\]+$/i, "")
            .trim() || "recording";
        return new File([file], `${base}.mp4`, {
          type: "video/mp4",
          lastModified: file.lastModified,
        });
      });
    if (!selected.length) return;
    const setter = target === "edit" ? setEditForm : setCreateForm;
    const existingCount =
      target === "edit"
        ? Number(detail?.photo_count || detail?.photos?.length || 0) || 0
        : 0;
    setter((prev) => {
      const remaining = MAX_PHOTOS - existingCount - (prev.photos?.length || 0);
      if (remaining <= 0) {
        toast.showError(`Maximum ${MAX_PHOTOS} photos allowed`);
        return prev;
      }
      if (selected.length > remaining) {
        toast.showError(`Maximum ${MAX_PHOTOS} photos allowed`);
      }
      const nextFiles = selected.slice(0, remaining);
      const nextSources = nextFiles.map(() => "Gallery");
      return {
        ...prev,
        photos: [...(prev.photos || []), ...nextFiles],
        sources: [...(prev.sources || []), ...nextSources],
      };
    });
  };

  const removePhoto = (index, target = "create") => {
    const setter = target === "edit" ? setEditForm : setCreateForm;
    setter((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
      sources: prev.sources.filter((_, i) => i !== index),
    }));
  };

  const handleCreate = async () => {
    if (
      !createForm.title.trim() ||
      !createForm.moduleFeature ||
      !createForm.priority ||
      !createForm.description.trim()
    ) {
      toast.showError("Please fill all required fields");
      return;
    }
    if (!createForm.photos.length) {
      toast.showError("Attachment is required");
      return;
    }

    setCreating(true);
    try {
      // Multipart form-data keys (same as API --form), not URL query params
      const formData = new FormData();
      formData.append("subject", createForm.title.trim());
      formData.append("issue_type", createForm.issueType || ISSUE_TYPE_OPTIONS[0]);
      formData.append("module_feature", createForm.moduleFeature);
      formData.append(
        "environment",
        createForm.environment || ENVIRONMENT_OPTIONS[0] || ""
      );
      formData.append("priority", createForm.priority);
      formData.append("description", createForm.description.trim());
      for (const file of createForm.photos) {
        const { blob, filename } = await preparePhotoUploadPart(file);
        // Third arg forces Content-Disposition filename (must not stay .mov)
        formData.append("photos", blob, filename);
      }

      const response = await apiService.postMultipart(
        API_ENDPOINTS.POST_create_support_request,
        formData
      );

      if (response?.success === false) {
        throw response;
      }
      toast.showSuccess(response?.message || "Support request created");
      notifySupportRequestCreated({
        title: createForm.title.trim(),
        description: createForm.description.trim(),
      });
      setCreateOpen(false);
      setCreateForm(emptyForm());
      setCurrentPage(1);
      fetchTickets();
      fetchStatusCount(statusFilter);
    } catch (error) {
      console.error("Create support request failed:", error);
      toast.showError(getApiErrorMessage(error, "Failed to create support request"));
    } finally {
      setCreating(false);
    }
  };

  const loadDetail = async (ticket) => {
    const ticketCode = getTicketCode(ticket);
    const supportId = getTicketId(ticket);
    if (!ticketCode) {
      toast.showError("Support request id not found");
      return;
    }
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    setStatusLogs([]);
    setStatusForm({
      status: "",
      remark: "",
      assigned_to: localStorage.getItem("user_id") || "",
      resolution: "",
    });
    try {
      const [detailRes, logsRes] = await Promise.all([
        apiService.get(
          `${API_ENDPOINTS.GET_support_request_by_id}?ticket_id=${encodeURIComponent(ticketCode)}`
        ),
        apiService
          .get(
            `${API_ENDPOINTS.GET_support_status_logs}?support_id=${encodeURIComponent(
              supportId || ticketCode
            )}`
          )
          .catch(() => null),
      ]);
      const detailData = extractDetail(detailRes) || ticket;
      setDetail(detailData);
      const logsFromApi = extractRows(logsRes);
      const logsFromDetail = detailData?.status_logs || detailData?.logs || [];
      setStatusLogs(
        logsFromApi.length > 0
          ? logsFromApi
          : Array.isArray(logsFromDetail)
          ? logsFromDetail
          : []
      );
      const flowNext = getNextStatusesForTicket(detailData);
      const next = flowNext[0] || "";
      setStatusForm({
        status: next,
        remark: "",
        resolution: "",
        assigned_to:
          detailData?.assigned_to != null
            ? String(detailData.assigned_to)
            : localStorage.getItem("user_id") || "",
      });
    } catch (error) {
      toast.showError(getApiErrorMessage(error, "Failed to load support request"));
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    const supportId = getTicketId(detail);
    const assignedToRaw = statusForm.assigned_to || localStorage.getItem("user_id");
    const assignedTo = Number(assignedToRaw);
    if (!supportId || !statusForm.status) {
      toast.showError("Select a status to update");
      return;
    }

    // Closed → Reopened: only original raiser, via reopen API
    if (
      isClosedTicket(detail) &&
      String(statusForm.status).toLowerCase() === "reopened"
    ) {
      if (!isTicketRaisedByCurrentUser(detail)) {
        toast.showError("Only the user who raised this ticket can reopen it");
        return;
      }
      setUpdatingStatus(true);
      try {
        const response = await apiService.put(
          `${API_ENDPOINTS.PUT_reopen_support_request}?support_id=${encodeURIComponent(supportId)}`,
          {
            support_id: supportId,
            remark: statusForm.remark.trim() || "Ticket reopened by requester",
          }
        );
        if (response?.success === false) throw response;
        toast.showSuccess(response?.message || "Support request reopened");
        setDetailOpen(false);
        setStatusUpdateOpen(false);
        refreshStatusCountsAfterMove(detail?.status, "Reopened");
        fetchTickets();
      } catch (error) {
        toast.showError(getApiErrorMessage(error, "Failed to reopen support request"));
      } finally {
        setUpdatingStatus(false);
      }
      return;
    }

    if (!assignedToRaw || Number.isNaN(assignedTo)) {
      toast.showError("Assigned to (user id) is required");
      return;
    }
    if (statusForm.status === "Resolved" && !statusForm.resolution.trim()) {
      toast.showError("Resolution is required when marking a ticket as Resolved");
      return;
    }
    setUpdatingStatus(true);
    try {
      const payload = {
        support_id: Number(supportId) || supportId,
        status: statusForm.status,
        assigned_to: assignedTo,
        remark: statusForm.remark.trim(),
      };
      if (statusForm.status === "Resolved") {
        payload.resolution = statusForm.resolution.trim();
      }
      const response = await apiService.put(
        API_ENDPOINTS.PUT_update_support_status,
        payload
      );
      if (response?.success === false) throw response;
      toast.showSuccess(response?.message || "Status updated");
      setDetailOpen(false);
      setStatusUpdateOpen(false);
      refreshStatusCountsAfterMove(detail?.status, statusForm.status);
      fetchTickets();
    } catch (error) {
      toast.showError(getApiErrorMessage(error, "Failed to update status"));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleReopen = async () => {
    const supportId = getTicketId(detail);
    if (!supportId) {
      toast.showError("Support request id not found");
      return;
    }
    if (isClosedTicket(detail) && !isTicketRaisedByCurrentUser(detail)) {
      toast.showError("Only the user who raised this ticket can reopen it");
      return;
    }
    setUpdatingStatus(true);
    try {
      const response = await apiService.put(
        `${API_ENDPOINTS.PUT_reopen_support_request}?support_id=${encodeURIComponent(supportId)}`,
        {
          support_id: supportId,
          remark: statusForm.remark.trim() || "Ticket reopened by requester",
        }
      );
      if (response?.success === false) throw response;
      toast.showSuccess(response?.message || "Support request reopened");
      setDetailOpen(false);
      refreshStatusCountsAfterMove(detail?.status, "Reopened");
      fetchTickets();
    } catch (error) {
      toast.showError(getApiErrorMessage(error, "Failed to reopen support request"));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openEdit = () => {
    if (!canEditRequest || !detail) return;
    setEditForm({
      title: detail.subject || detail.title || "",
      description: detail.description || "",
      photos: [],
      sources: [],
      issueType: detail.issue_type || "",
      moduleFeature: detail.module_feature || "",
      environment: detail.environment || "",
      priority: detail.priority || "",
    });
    setEditOpen(true);
  };

  const handleDetailInfoChange = async (field, value) => {
    if (!canEditRequest || !detail) return;
    const supportId = getTicketId(detail);
    if (!supportId) {
      toast.showError("Support request id not found");
      return;
    }

    const prevDetail = detail;
    const nextModule =
      field === "module_feature" ? value : detail.module_feature || "";
    const nextPriority = field === "priority" ? value : detail.priority || "";

    setDetail((p) =>
      p
        ? {
            ...p,
            module_feature: nextModule,
            priority: nextPriority,
          }
        : p
    );

    try {
      const response = await apiService.put(API_ENDPOINTS.PUT_update_support_request, {
        support_id: supportId,
        title: getTicketSubject(detail),
        description: detail.description || "",
        module_feature: nextModule,
        priority: nextPriority,
      });
      if (response?.success === false) throw response;
      toast.showSuccess(response?.message || "Support request updated");
      fetchTickets();
    } catch (error) {
      setDetail(prevDetail);
      toast.showError(getApiErrorMessage(error, "Failed to update support request"));
    }
  };

  const handleEdit = async () => {
    const supportId = getTicketId(detail);
    if (!supportId) {
      toast.showError("Support request id not found");
      return;
    }
    if (!editForm.title.trim() || !editForm.description.trim()) {
      toast.showError("Title and description are required");
      return;
    }
    setEditing(true);
    try {
      // API contract: JSON body with support_id, title, description
      const response = await apiService.put(API_ENDPOINTS.PUT_update_support_request, {
        support_id: supportId,
        title: editForm.title.trim(),
        description: editForm.description.trim(),
      });
      if (response?.success === false) throw response;
      toast.showSuccess(response?.message || "Support request updated");
      setEditOpen(false);
      await loadDetail(detail);
      fetchTickets();
      fetchStatusCount(statusFilter);
    } catch (error) {
      toast.showError(getApiErrorMessage(error, "Failed to update support request"));
    } finally {
      setEditing(false);
    }
  };

  const handleDelete = async () => {
    const ticket = deleteDialog.ticket;
    const supportId = getTicketId(ticket);
    if (!supportId) return;
    setDeleting(true);
    try {
      const response = await apiService.delete(
        `${API_ENDPOINTS.DELETE_support_request}?support_id=${encodeURIComponent(supportId)}`
      );
      if (response?.success === false) throw response;
      toast.showSuccess(response?.message || "Support request deleted");
      setDeleteDialog({ open: false, ticket: null });
      if (getTicketId(detail) === supportId) {
        setDetailOpen(false);
        setDetail(null);
      }
      fetchTickets();
      fetchStatusCount(statusFilter);
    } catch (error) {
      toast.showError(getApiErrorMessage(error, "Failed to delete support request"));
    } finally {
      setDeleting(false);
    }
  };

  const openTicketPhotos = async (ticket) => {
    const photoCount = Number(ticket.photo_count ?? ticket.photos?.length ?? 0);
    if (photoCount <= 0) {
      toast.showError("No photos available");
      return;
    }

    let urls = resolvePhotoUrls(ticket);
    if (urls.length === 0) {
      const ticketCode = getTicketCode(ticket);
      if (!ticketCode) {
        toast.showError("Support request id not found");
        return;
      }

      setPhotoPreview({ open: true, urls: [], loading: true });
      try {
        const detailRes = await apiService.get(
          `${API_ENDPOINTS.GET_support_request_by_id}?ticket_id=${encodeURIComponent(ticketCode)}`
        );
        const detailData = extractDetail(detailRes) || ticket;
        urls = resolvePhotoUrls(detailData);
      } catch (error) {
        toast.showError(getApiErrorMessage(error, "Failed to load photos"));
        setPhotoPreview({ open: false, urls: [], loading: false });
        return;
      }
    } else {
      setPhotoPreview({ open: true, urls, loading: false });
      return;
    }

    if (!urls.length) {
      toast.showError("No photos available");
      setPhotoPreview({ open: false, urls: [], loading: false });
      return;
    }

    setPhotoPreview({ open: true, urls, loading: false });
  };

  const photoUrls = useMemo(() => resolvePhotoUrls(detail), [detail]);

  const isPending =
    ["pending", "new"].includes(String(detail?.status || "").toLowerCase());
  const detailStatus = String(detail?.status || "").toLowerCase();
  const canReopenStatus = ["closed", "done", "resolved", "complete"].includes(
    detailStatus
  );
  // Closed: only original raiser can reopen; other statuses unchanged
  const canReopen =
    canReopenStatus &&
    (detailStatus !== "closed" || isTicketRaisedByCurrentUser(detail));

  return (
    <Box pt={1.5} pb={3}>
      <FlexBetween alignItems="flex-start" flexWrap="wrap" gap={1.5} mb={2.5}>
        <Box>
          {/* <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.35rem", sm: "1.6rem" },
              color: "text.primary",
              letterSpacing: "-0.02em",
              mb: 0.5,
            }}
          >
            Raise Support Ticket
          </Typography> */}
          {/* <Typography variant="body2" color="text.secondary">
            Submit a ticket to get help from the support team
          </Typography> */}
        </Box>
        <FlexBox alignItems="center" gap={1.5} flexWrap="wrap">
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500, whiteSpace: "nowrap" }}
          >
            Support{" "}
            <Box component="span" sx={{ mx: 0.5, color: "text.disabled" }}>
              &gt;
            </Box>{" "}
            Tickets{" "}
            <Box component="span" sx={{ mx: 0.5, color: "text.disabled" }}>
              &gt;
            </Box>{" "}
            <Box component="span" sx={{ color: "primary.main", fontWeight: 600 }}>
              Raise New Ticket
            </Box>
          </Typography>
        </FlexBox>
      </FlexBetween>

      {canCreate ? (
        <Dialog
          open={createOpen}
          onClose={() => !creating && handleCancelCreate()}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              maxWidth: 1100,
            },
          }}
        >
          <DialogTitle sx={{ px: 3, py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Add sx={{ color: "primary.main", fontSize: 26 }} />
                <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: "-0.01em" }}>
                  Raise New Ticket
                </Typography>
              </Box>
              <IconButton onClick={handleCancelCreate} disabled={creating} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            dividers
            sx={{
              px: 3,
              py: 2.5,
              maxHeight: "75vh",
              overflowY: "auto",
              bgcolor: "#fff",
            }}
          >
            <Grid container spacing={2.5} alignItems="flex-start">
              <Grid item xs={12} sm={4}>
                <Box sx={{ minHeight: 108 }}>
                  <FieldLabel step={1} label="Ticket Subject" required />
                  <StyledFormField
                    fullWidth
                    size="small"
                    placeholder="Enter a short and clear title"
                    value={createForm.title}
                    onChange={(e) => setCreateForm((p) => ({ ...p, title: e.target.value }))}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.75, display: "block", lineHeight: 1.35 }}
                  >
                    Example: Payment status is not updating after successful payment.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Box sx={{ minHeight: 108 }}>
                  <FieldLabel step={3} label="Module / Feature" required />
                  <StyledSelect fullWidth size="small">
                    <InputLabel>Select module / feature</InputLabel>
                    <Select
                      label="Select module / feature"
                      value={createForm.moduleFeature}
                      onChange={(e) =>
                        setCreateForm((p) => ({ ...p, moduleFeature: e.target.value }))
                      }
                    >
                      {visibleModuleFeatureOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledSelect>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.75, display: "block", lineHeight: 1.35 }}
                  >
                    Select where the issue is occurring.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Box sx={{ minHeight: 108 }}>
                  <FieldLabel step={4} label="Priority" required />
                  <StyledSelect fullWidth size="small">
                    <InputLabel>Select priority</InputLabel>
                    <Select
                      label="Select priority"
                      value={createForm.priority}
                      onChange={(e) =>
                        setCreateForm((p) => ({ ...p, priority: e.target.value }))
                      }
                      renderValue={(selected) => selected || "Select priority"}
                    >
                      {PRIORITY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {option.value}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.helper}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledSelect>
                </Box>
              </Grid>

              <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
                  <FieldLabel step={6} label="Attachment" required />
                  <Box
                    component="label"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      flex: 1,
                      width: "100%",
                      minHeight: 260,
                      px: 1.5,
                      py: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                      cursor:
                        createForm.photos.length >= MAX_PHOTOS ? "not-allowed" : "pointer",
                      opacity: createForm.photos.length >= MAX_PHOTOS ? 0.6 : 1,
                      transition: "border-color 0.2s ease, background-color 0.2s ease",
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
                      },
                    }}
                  >
                    <CloudUploadOutlined sx={{ fontSize: 30, color: "primary.main" }} />
                    <Typography variant="body2" fontWeight={600} textAlign="center">
                      Drag & drop files here or{" "}
                      <Box component="span" sx={{ color: "primary.main" }}>
                        Browse Files
                      </Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                      (Max 150MB · JPG, PNG, PDF, DOC, MP4/MOV) — up to {MAX_PHOTOS} photos
                    </Typography>
                    <input
                      hidden
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      disabled={createForm.photos.length >= MAX_PHOTOS}
                      onChange={(e) => {
                        handlePhotoSelect(e.target.files, "create");
                        e.target.value = "";
                      }}
                    />
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: "wrap" }}>
                    {createForm.photos.map((file, index) => (
                      <Chip
                        key={`${file.name}-${index}`}
                        label={file.name}
                        onDelete={() => removePhoto(index, "create")}
                        size="small"
                      />
                    ))}
                  </Stack>
                </Box>
              </Grid>

              <Grid item xs={12} md={8} sx={{ display: "flex" }}>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
                  <FieldLabel step={5} label="Description" required />
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      overflow: "hidden",
                      bgcolor: "background.paper",
                      flex: 1,
                      minHeight: 260,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                  <Stack
                    direction="row"
                    spacing={0.25}
                    alignItems="center"
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ px: 1, color: "text.secondary", fontWeight: 600 }}
                    >
                      Normal
                    </Typography>
                    <IconButton size="small" disabled>
                      <FormatBold fontSize="small" />
                    </IconButton>
                    <IconButton size="small" disabled>
                      <FormatItalic fontSize="small" />
                    </IconButton>
                    <IconButton size="small" disabled>
                      <FormatUnderlined fontSize="small" />
                    </IconButton>
                    <IconButton size="small" disabled>
                      <FormatListBulleted fontSize="small" />
                    </IconButton>
                    <IconButton size="small" disabled>
                      <FormatListNumbered fontSize="small" />
                    </IconButton>
                    <IconButton size="small" disabled>
                      <InsertLink fontSize="small" />
                    </IconButton>
                  </Stack>
                  <TextField
                    fullWidth
                    multiline
                    minRows={9}
                    placeholder={
                      "Explain the issue clearly.\n- What is happening?\n- What should happen?\n- What is the actual result?"
                    }
                    value={createForm.description}
                    onChange={(e) =>
                      setCreateForm((p) => ({ ...p, description: e.target.value }))
                    }
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      px: 1.5,
                      py: 1,
                      "& .MuiInputBase-input": { fontSize: "0.9rem" },
                    }}
                  />
                </Box>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1.25 }}>
            <Button
              onClick={handleCancelCreate}
              disabled={creating}
              sx={{ textTransform: "none", fontWeight: 600, color: "text.primary" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={creating}
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <Add />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                minWidth: 150,
                borderRadius: 2,
                px: 2.5,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {creating ? "Submitting..." : "Submit Ticket"}
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <FormCard>
          <Typography color="text.secondary">
            You do not have permission to create support tickets.
          </Typography>
        </FormCard>
      )}

      {/* <WorkflowCard>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ mb: 2.5, color: "primary.main", letterSpacing: "-0.01em" }}
        >
          Developer Workflow (Ticket Life Cycle)
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 0.5,
            overflowX: "auto",
            pb: 0.5,
          }}
        >
          {WORKFLOW_STEPS.map((step, index) => {
            const Icon = step.icon;
            return (
              <Box
                key={step.title}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  flex: "1 1 0",
                  minWidth: 118,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    px: 0.25,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.75,
                      mb: 1,
                      minHeight: 28,
                    }}
                  >
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        color: "#fff",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 700,
                        flexShrink: 0,
                        lineHeight: 1,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{
                        color: "text.primary",
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        fontSize: "0.85rem",
                      }}
                    >
                      {step.title}
                    </Typography>
                  </Box>

                  <Icon
                    sx={{
                      fontSize: 26,
                      color: "primary.main",
                      mb: 0.75,
                    }}
                  />

                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "text.secondary",
                      lineHeight: 1.35,
                      maxWidth: 120,
                      fontSize: "0.72rem",
                    }}
                  >
                    {step.desc}
                  </Typography>
                </Box>

                {index < WORKFLOW_STEPS.length - 1 ? (
                  <Box
                    sx={{
                      display: { xs: "none", md: "flex" },
                      alignItems: "center",
                      alignSelf: "center",
                      mt: -2.5,
                      mx: 0.25,
                      flexShrink: 0,
                      minWidth: 28,
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        height: 0,
                        borderTop: "1.5px dashed",
                        borderColor: "divider",
                        minWidth: 14,
                      }}
                    />
                    <ArrowForward
                      sx={{
                        color: "text.disabled",
                        fontSize: 14,
                        ml: -0.25,
                      }}
                    />
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Box>
      </WorkflowCard> */}

      <FilterCard>
        <FilterHeader>
          <FlexBetween gap={1} flexWrap="wrap">
            <Typography variant="subtitle2" fontWeight={700} color="primary">
              Ticket Details After Creation
            </Typography>
            <FlexBox alignItems="center" gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={() => {
                  fetchTickets();
                  fetchStatusCount(statusFilter);
                }}
                disabled={loading}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Refresh
              </Button>
              {canCreate ? (
                <Tooltip title="Raise a new support ticket">
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      onClick={openCreate}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Raise New Ticket
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
            </FlexBox>
          </FlexBetween>

          <Card
            sx={{
              px: 1.5,
              py: 1.5,
              mt: 1.25,
              borderRadius: 1.5,
              boxShadow: "none",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 0.5,
                overflowX: "auto",
                pb: 0.5,
              }}
            >
              {statusTabs.map((tab, index) => {
                const stepIndex = WORKFLOW_STEPS.findIndex(
                  (step) => step.title.toLowerCase() === String(tab.label).toLowerCase()
                );
                const step = stepIndex >= 0 ? WORKFLOW_STEPS[stepIndex] : null;
                const Icon = step?.icon || (tab.value === "all" ? LayersOutlined : null);
                const active = statusFilter === tab.value;
                const count =
                  stepIndex >= 0 || tab.value === "all"
                    ? Number(statusCounts[tab.value] ?? 0)
                    : null;
                return (
                  <Box
                    key={tab.value}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      flex: "1 1 0",
                      minWidth: 118,
                    }}
                  >
                    <Box
                      component="button"
                      type="button"
                      onClick={() => {
                        setStatusFilter(tab.value);
                        setCurrentPage(1);
                      }}
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        px: 0.25,
                        py: 0.5,
                        border: "none",
                        background: active
                          ? (theme) => alpha(theme.palette.primary.main, 0.06)
                          : "transparent",
                        borderRadius: 1.5,
                        cursor: "pointer",
                        "&:hover": {
                          background: (theme) => alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.75,
                          mb: 1,
                          minHeight: 28,
                        }}
                      >
                        {count != null ? (
                          <Box
                            sx={{
                              width: 22,
                              height: 22,
                              borderRadius: "50%",
                              bgcolor: "primary.main",
                              color: "#fff",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 11,
                              fontWeight: 700,
                              flexShrink: 0,
                              lineHeight: 1,
                            }}
                          >
                            {count}
                          </Box>
                        ) : null}
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{
                            color: active ? "primary.main" : "text.primary",
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            fontSize: "0.85rem",
                          }}
                        >
                          {tab.label}
                        </Typography>
                      </Box>

                      {Icon ? (
                        <Icon
                          sx={{
                            fontSize: 26,
                            color: "primary.main",
                            mb: 0.75,
                          }}
                        />
                      ) : null}

                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "text.secondary",
                          lineHeight: 1.35,
                          maxWidth: 120,
                          fontSize: "0.72rem",
                        }}
                      >
                        {step?.desc ||
                          (tab.value === "all" ? "All tickets" : tab.label)}
                      </Typography>
                    </Box>

                    {index < statusTabs.length - 1 ? (
                      <Box
                        sx={{
                          display: { xs: "none", md: "flex" },
                          alignItems: "center",
                          alignSelf: "center",
                          mt: -2.5,
                          mx: 0.25,
                          flexShrink: 0,
                          minWidth: 28,
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            height: 0,
                            borderTop: "1.5px dashed",
                            borderColor: "divider",
                            minWidth: 14,
                          }}
                        />
                        <ArrowForward
                          sx={{
                            color: "text.disabled",
                            fontSize: 14,
                            ml: -0.25,
                          }}
                        />
                      </Box>
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </Card>
        </FilterHeader>

        <Box sx={{ px: 1.5, py: 1.25 }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} md={8}>
              <FlexBox alignItems="center" gap={1}>
                <SearchInput
                  placeholder="Search by title or description..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setSearchTerm(searchValue);
                      setCurrentPage(1);
                    }
                  }}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    border: "1px solid",
                    borderColor: "grey.300",
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: "background.paper",
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={() => {
                    setSearchTerm(searchValue);
                    setCurrentPage(1);
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    height: 40,
                    minWidth: 96,
                    whiteSpace: "nowrap",
                  }}
                >
                  Search
                </Button>
              </FlexBox>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Rows</InputLabel>
                <Select
                  label="Rows"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {pageSizeOptions.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </FilterCard>

      <Card
        sx={{
          marginTop: 0,
          borderRadius: 1.5,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
        }}
      >
        <TableContainer
          sx={(theme) => ({
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: "calc(100vh - 320px)",
            scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark" ? "#555 #1e1e1e" : "#c1c1c1 #f1f1f1",
            "&::-webkit-scrollbar": { height: 8, width: 8 },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.mode === "dark" ? "#555" : "#c1c1c1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: theme.palette.mode === "dark" ? "#777" : "#a8a8a8",
            },
          })}
        >
          <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                {[
                  "Ticket ID",
                  "Status",
                  "Created Date & Time",
                  "Priority",
                  "Last Updated",
                  "Raised By",
                  "Resolution",
                  "Actions",
                ].map((heading) => (
                  <TableCell
                    key={heading}
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      whiteSpace: "nowrap",
                      bgcolor: (theme) => theme.palette.grey[200],
                      color: "text.primary",
                      py: 1.5,
                      px: 1.5,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                  >
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ border: "none" }}>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
                      <CircularProgress size={36} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : tickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ border: "none" }}>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
                      <Typography color="text.secondary">No support requests found</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                tickets.map((ticket) => {
                  const id = getTicketId(ticket);
                  const priorityValue =
                    ticket.priority || ticket.ticket_priority || ticket.urgency || "—";
                  const raisedBy =
                    ticket.raised_by_details?.full_name ||
                    ticket.raised_by_name ||
                    ticket.created_by_name ||
                    ticket.user_name ||
                    "—";
                  const resolution =
                    ticket.resolution || ticket.remark || ticket.latest_remark || "—";
                  const lastUpdated =
                    ticket.updated_at || ticket.modified_at || ticket.created_at || ticket.created_on;
                  return (
                    <TableRow
                      key={id}
                      hover
                      sx={{
                        "&:hover": {
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                        },
                        "& > .MuiTableCell-root": {
                          borderBottom: (theme) =>
                            `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                          py: 1.5,
                          px: 1.5,
                          verticalAlign: "middle",
                          fontSize: "0.8rem",
                        },
                      }}
                    >
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                      >
                        {formatDisplayTicketId(ticket)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={ticket.status || "—"}
                          color={getStatusColor(ticket.status)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        {formatDateTime(ticket.created_at || ticket.created_on)}
                      </TableCell>
                      <TableCell align="center">
                        {priorityValue !== "—" ? (
                          <Chip
                            size="small"
                            label={priorityValue}
                            color={getPriorityChipColor(priorityValue)}
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        {formatDateTime(lastUpdated)}
                      </TableCell>
                      <TableCell align="center" sx={{ maxWidth: 140 }}>
                        <Typography variant="body2" noWrap title={String(raisedBy)}>
                          {raisedBy}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap title={String(resolution)}>
                          {resolution}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Tooltip
                            title={
                              !canUpdateStatus
                                ? "No permission to update status"
                                : isClosedTicket(ticket) &&
                                    isTicketRaisedByCurrentUser(ticket)
                                  ? "Reopen Ticket"
                                  : getNextStatusesForTicket(ticket).length === 0
                                    ? "No status update available"
                                    : "Update Status"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openStatusUpdate(ticket)}
                                disabled={
                                  !canUpdateStatus ||
                                  getNextStatusesForTicket(ticket).length === 0
                                }
                                sx={{
                                  opacity:
                                    !canUpdateStatus ||
                                    getNextStatusesForTicket(ticket).length === 0
                                      ? 0.5
                                      : 1,
                                }}
                              >
                                <History fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {(ticket.photo_count ?? ticket.photos?.length ?? 0) > 0 ? (
                            <Tooltip title="View photos">
                              <IconButton size="small" onClick={() => openTicketPhotos(ticket)}>
                                <ImageOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          <Tooltip title={canView ? "View" : "No permission to view"}>
                            <span>
                              <IconButton
                                size="small"
                                color="viewBlue"
                                onClick={() => {
                                  if (!canView) return;
                                  loadDetail(ticket);
                                }}
                                disabled={!canView}
                                sx={{ opacity: !canView ? 0.5 : 1 }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={canDelete ? "Delete" : "No permission to delete"}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  if (!canDelete) return;
                                  setDeleteDialog({ open: true, ticket });
                                }}
                                disabled={!canDelete}
                                sx={{ opacity: !canDelete ? 0.5 : 1 }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {!loading && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {totalItems > 0
              ? `Showing ${(currentPage - 1) * pageSize + 1} - ${Math.min(
                  currentPage * pageSize,
                  totalItems
                )} of ${totalItems} requests`
              : "Showing 0 of 0 requests"}
          </Typography>
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <AppPagination
                shape="rounded"
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          )}
        </Box>
      )}

      {/* Detail dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => !updatingStatus && setDetailOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(15, 23, 42, 0.12)",
          },
        }}
      >
        <DialogTitle sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Visibility sx={{ color: "primary.main", fontSize: 24 }} />
              <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: "-0.01em" }}>
                Support Request Details
              </Typography>
            </Box>
            <IconButton onClick={() => setDetailOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.015),
          }}
        >
          {detailLoading || !detail ? (
            <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2.25}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <FlexBetween alignItems="flex-start" gap={1.5} flexWrap="wrap">
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ letterSpacing: "-0.01em", lineHeight: 1.35, mb: 0.75 }}
                    >
                      {getTicketSubject(detail)}
                    </Typography>
                    <FlexBox alignItems="center" gap={1} flexWrap="wrap">
                      <Chip
                        size="small"
                        label={formatDisplayTicketId(detail)}
                        sx={{
                          fontWeight: 600,
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                          color: "primary.main",
                          borderRadius: 1.5,
                        }}
                      />
                      <Chip
                        size="small"
                        label={detail.status || "—"}
                        color={getStatusColor(detail.status)}
                        sx={{ fontWeight: 600, borderRadius: 1.5 }}
                      />
                    </FlexBox>
                  </Box>
                </FlexBetween>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ mb: 1.5 }}
                >
                  Ticket Information
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 0.75, fontWeight: 600 }}
                    >
                      Module / Feature
                    </Typography>
                    <StyledSelect fullWidth size="small">
                      <Select
                        displayEmpty
                        value={detail.module_feature || ""}
                        disabled={!canEditRequest}
                        onChange={(e) =>
                          handleDetailInfoChange("module_feature", e.target.value)
                        }
                        renderValue={(selected) =>
                          selected || (
                            <Box component="span" sx={{ color: "text.secondary" }}>
                              Select module / feature
                            </Box>
                          )
                        }
                      >
                        {detail.module_feature &&
                        !visibleModuleFeatureOptions.includes(detail.module_feature) ? (
                          <MenuItem value={detail.module_feature}>
                            {detail.module_feature}
                          </MenuItem>
                        ) : null}
                        {visibleModuleFeatureOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </StyledSelect>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 0.75, fontWeight: 600 }}
                    >
                      Priority
                    </Typography>
                    <StyledSelect fullWidth size="small">
                      <Select
                        displayEmpty
                        value={detail.priority || ""}
                        disabled={!canEditRequest}
                        onChange={(e) =>
                          handleDetailInfoChange("priority", e.target.value)
                        }
                        renderValue={(selected) =>
                          selected || (
                            <Box component="span" sx={{ color: "text.secondary" }}>
                              Select priority
                            </Box>
                          )
                        }
                      >
                        {PRIORITY_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {option.value}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.helper}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </StyledSelect>
                  </Grid>
                </Grid>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ mb: 1 }}
                >
                  Description
                </Typography>
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.7,
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {detail.description}
                </Typography>
              </Box>

              {photoUrls.length > 0 && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="primary.main"
                    sx={{ mb: 1.25 }}
                  >
                    Attachments
                  </Typography>
                  <Grid container spacing={1.25}>
                    {photoUrls.map((url, index) => (
                      <Grid item xs={6} sm={4} key={`${url}-${index}`}>
                        <Box
                          component="a"
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          sx={{
                            display: "block",
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: "divider",
                            height: 148,
                            transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                            "&:hover": {
                              borderColor: "primary.main",
                              boxShadow: (theme) =>
                                `0 4px 14px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                          }}
                        >
                          <AttachmentMedia url={url} index={index} />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {isPending && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Tooltip
                    title={
                      canEditRequest
                        ? "Edit Request"
                        : "No permission to edit request"
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<EditOutlined />}
                        onClick={openEdit}
                        disabled={!canEditRequest}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 2,
                          opacity: !canEditRequest ? 0.6 : 1,
                        }}
                      >
                        Edit Request
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              )}

              {canUpdateStatus && canReopen && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: (theme) => alpha(theme.palette.warning.main, 0.35),
                    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.04),
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Reopen Ticket
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Add a remark and reopen this ticket if the issue is not resolved.
                  </Typography>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={9}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Remark"
                        placeholder="Ticket reopened by requester"
                        value={statusForm.remark}
                        onChange={(e) =>
                          setStatusForm((p) => ({ ...p, remark: e.target.value }))
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "background.paper",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="warning"
                        startIcon={<ReplayOutlined />}
                        disabled={updatingStatus}
                        onClick={handleReopen}
                        sx={{ textTransform: "none", height: 40, fontWeight: 600, borderRadius: 2 }}
                      >
                        {updatingStatus ? <CircularProgress size={18} /> : "Reopen"}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Status Logs
                </Typography>
                {statusLogs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No status logs yet
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {statusLogs.map((log, index) => (
                      <Box
                        key={log.id || index}
                        sx={{
                          p: 1.25,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: alpha("#000", 0.02),
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {log.status || log.new_status || log.to_status || "Status updated"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {log.remark || log.comment || log.note || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[
                            log.updated_by_name ||
                              log.updated_by ||
                              log.user_name ||
                              log.created_by,
                            (() => {
                              const rawDate =
                                log.created_at || log.updated_at || log.timestamp;
                              if (!rawDate) return null;
                              const parsed = new Date(rawDate);
                              return Number.isNaN(parsed.getTime())
                                ? rawDate
                                : parsed.toLocaleString();
                            })(),
                          ]
                            .filter(Boolean)
                            .join(" • ")}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box> */}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => !editing && setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Support Request</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Title"
              required
              fullWidth
              value={editForm.title}
              onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
            />
            <TextField
              label="Description"
              required
              fullWidth
              multiline
              minRows={4}
              value={editForm.description}
              onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditOpen(false)} disabled={editing}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleEdit} disabled={editing}>
            {editing ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={statusUpdateOpen}
        onClose={() => !updatingStatus && setStatusUpdateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Update Status
          <IconButton
            onClick={() => setStatusUpdateOpen(false)}
            size="small"
            disabled={updatingStatus}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {formatDisplayTicketId(detail)} · {detail?.status || "—"}
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusForm.status}
                  onChange={(e) =>
                    setStatusForm((p) => ({
                      ...p,
                      status: e.target.value,
                      resolution: e.target.value === "Resolved" ? p.resolution : "",
                    }))
                  }
                >
                  {nextStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Remark"
                value={statusForm.remark}
                onChange={(e) =>
                  setStatusForm((p) => ({ ...p, remark: e.target.value }))
                }
              />
            </Grid>
            {statusForm.status === "Resolved" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  required
                  label="Resolution *"
                  placeholder="Describe how the issue was resolved"
                  value={statusForm.resolution}
                  onChange={(e) =>
                    setStatusForm((p) => ({
                      ...p,
                      resolution: e.target.value,
                    }))
                  }
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setStatusUpdateOpen(false)}
            disabled={updatingStatus}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={updatingStatus || !statusForm.status}
            onClick={handleUpdateStatus}
            sx={{ textTransform: "none" }}
          >
            {updatingStatus ? <CircularProgress size={18} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={photoPreview.open}
        onClose={() => setPhotoPreview({ open: false, urls: [], loading: false })}
        maxWidth={photoPreview.urls.length >= 3 ? "xl" : "md"}
        fullWidth
        PaperProps={{
          sx: {
            width:
              photoPreview.urls.length <= 1
                ? 720
                : photoPreview.urls.length === 2
                  ? 900
                  : 1280,
            maxWidth: "calc(100% - 48px)",
          },
        }}
      >
        <DialogTitle>
          <FlexBetween>
            Photos
            <IconButton
              size="small"
              onClick={() => setPhotoPreview({ open: false, urls: [], loading: false })}
            >
              <Close />
            </IconButton>
          </FlexBetween>
        </DialogTitle>
        <DialogContent dividers>
          {photoPreview.loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Grid container spacing={1.5}>
              {photoPreview.urls.map((url, index) => (
                <Grid
                  item
                  xs={12}
                  sm={photoPreview.urls.length === 1 ? 12 : 6}
                  md={
                    photoPreview.urls.length <= 1
                      ? 12
                      : photoPreview.urls.length === 2
                        ? 6
                        : 4
                  }
                  key={`${url}-${index}`}
                >
                  <Box
                    sx={{
                      borderRadius: 1,
                      overflow: "hidden",
                      border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      height: photoPreview.urls.length >= 3 ? 360 : 420,
                      bgcolor: "grey.100",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AttachmentMedia
                      url={url}
                      index={index}
                      sx={{
                        maxHeight: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={deleteDialog.open}
        onClose={() => !deleting && setDeleteDialog({ open: false, ticket: null })}
        onConfirm={handleDelete}
        title="Delete Support Request"
        message="Are you sure you want to delete this support request? This is a soft delete."
        confirmButtonText={deleting ? "Deleting..." : "Delete"}
        confirmButtonColor="error"
        isLoading={deleting}
      />
    </Box>
  );
};

export default Support;

